import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RepositoryPicker, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {useCallback} from 'react'

import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import type {CreateIssueProps} from './CreateIssue'
import {CreateIssueForm} from './CreateIssueForm'
import {useOnRepositoryChange} from './hooks/use-on-repository-change'
import type {OnCreateProps} from './utils/model'

export type CreateDuplicateIssueFormProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
} & Omit<CreateIssueProps, 'currentRepository'>

export function CreateDuplicateIssueForm({
  onCreateSuccess,
  topRepositories,
  issueFormRef,
  ...props
}: CreateDuplicateIssueFormProps) {
  const {title, body, setBody, repository, setNewTitle, clearSessionData} = useIssueCreateDataContext()
  const {onRepositoryChange, notice, sourceRepository} = useOnRepositoryChange()
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  const getEligibleRepositoriesForIssueDuplication = useCallback(
    (repo: RepositoryPickerRepository) => {
      if (repo.id === sourceRepository?.id) {
        // This condition is true when user selects the repository the current issue is in
        // and matches the access level required to see the Duplicate issue option (triage only).
        // An existing issue in the current repo should be safe to duplicate.
        return repo.viewerIssueCreationPermissions.triageable
      } else {
        return repo.viewerIssueCreationPermissions.triageable && repo.isBlankIssuesEnabled
      }
    },
    [sourceRepository?.id],
  )

  return (
    <>
      {topRepositories && repository && (
        <>
          <RepositoryPicker
            aria-describedby="Choose repository to duplicate issue in"
            initialRepository={repository}
            topRepositories={topRepositories}
            onSelect={onRepositoryChange}
            focusRepositoryPicker
            enforceAtleastOneSelected
            options={{hasIssuesEnabled: true}}
            notice={{text: notice, variant: 'warning'}}
            repositoryFilter={getEligibleRepositoriesForIssueDuplication}
            responsiveOnNarrow={responsiveOnNarrowEnabled}
          />
          <CreateIssueForm
            {...props}
            showSimilarIssues={false}
            issueFormRef={issueFormRef}
            onCreateSuccess={onCreateSuccess}
            repository={repository}
            title={title}
            setTitle={setNewTitle}
            body={body}
            setBody={setBody}
            clearOnCreate={clearSessionData}
            focusTitleInput={false}
          />
        </>
      )}
    </>
  )
}

try{ CreateDuplicateIssueForm.displayName ||= 'CreateDuplicateIssueForm' } catch {}